import React from 'react';
import { T } from '../../utils/theme';
import api, { API_URL } from '../../services/api';
import { Shuffle, ArrowLeft, Loader, User } from 'lucide-react';

const BarberStep = ({ branchId, onSelect, onBack }) => {
  const [barbers, setBarbers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await api.get(`/kiosk/barbers${branchId ? `?branchId=${branchId}` : ''}`);
        if (mounted) setBarbers(data);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [branchId]);

  if (loading) {
    return (
      <div style={{ minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: T.bg }}>
        <div style={{ textAlign: 'center', color: T.label2 }}>
          <Loader size={24} className="animate-spin" style={{ margin: '0 auto 12px' }} />
          <div style={{ fontSize: 15 }}>Carregando barbeiros...</div>
        </div>
      </div>
    );
  }

  const available = barbers.filter(b => b.isAvailable);

  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column',
      background: T.bg, overflow: 'hidden',
    }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '40px 24px', minHeight: 0 }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{
            width: 64, height: 64, borderRadius: 20, margin: '0 auto 20px',
            background: 'rgba(48,209,88,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <User size={28} color={T.success} />
          </div>
          <div style={{ fontSize: 28, fontWeight: 700, color: T.label, letterSpacing: '-0.025em' }}>Escolha o barbeiro</div>
          <div style={{ fontSize: 16, color: T.label2, marginTop: 6 }}>Selecione quem vai te atender</div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1, overflowY: 'auto', minHeight: 0, paddingBottom: 4 }}>
          {/* Random option */}
          <button onClick={() => onSelect('RANDOM')} style={{
            width: '100%', padding: 18, borderRadius: 18,
            background: `linear-gradient(135deg, ${T.accent}, ${T.indigo})`,
            border: 'none', cursor: 'pointer', textAlign: 'left',
            display: 'flex', alignItems: 'center', gap: 14,
            boxShadow: '0 4px 14px rgba(10,132,255,0.3)',
          }}>
            <div style={{
              width: 48, height: 48, borderRadius: 16,
              background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Shuffle size={22} color="#fff" />
            </div>
            <div>
              <div style={{ fontSize: 17, fontWeight: 600, color: '#fff' }}>Aleatorio</div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', marginTop: 2 }}>Entrar na menor fila disponivel</div>
            </div>
          </button>

          {/* Barber list */}
          {available.map(b => {
            const initials = (b.name || '').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
            const apiBase = API_URL.replace(/\/api\/?$/, '');
            const fotoUrl = b.foto ? `${apiBase}${b.foto}` : null;
            return (
              <button key={b.id} onClick={() => onSelect({ id: b.id, name: b.name })} style={{
                width: '100%', padding: 16, borderRadius: 18,
                background: '#fff', border: 'none', cursor: 'pointer',
                boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left',
              }}>
                {fotoUrl ? (
                  <img src={fotoUrl} alt={b.name} style={{
                    width: 48, height: 48, borderRadius: 16, objectFit: 'cover', objectPosition: 'center 20%', flexShrink: 0,
                  }} />
                ) : (
                  <div style={{
                    width: 48, height: 48, borderRadius: 16,
                    background: T.fill, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: 16, color: T.label, flexShrink: 0,
                  }}>{initials}</div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 600, color: T.label }}>{b.name}</div>
                  <div style={{ fontSize: 13, color: T.label2, marginTop: 2 }}>
                    {b.queueSize === 0 ? 'Fila vazia' : `${b.queueSize} na fila`}
                  </div>
                </div>
                <div style={{
                  padding: '4px 10px', borderRadius: 10,
                  background: b.queueSize === 0 ? 'rgba(48,209,88,0.12)' : T.fill,
                  fontSize: 12, fontWeight: 600,
                  color: b.queueSize === 0 ? T.success : T.label2,
                }}>{b.queueSize === 0 ? 'Livre' : b.queueSize}</div>
              </button>
            );
          })}

          {available.length === 0 && (
            <div style={{
              background: '#fff', borderRadius: 18, padding: 32, textAlign: 'center',
              boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            }}>
              <div style={{ fontSize: 15, color: T.label2 }}>Nenhum barbeiro disponivel no momento</div>
            </div>
          )}
        </div>

        <button onClick={onBack} style={{
          width: '100%', padding: 16, borderRadius: 16, marginTop: 16,
          border: `1px solid ${T.separator}`, background: '#fff', color: T.label,
          fontFamily: 'inherit', fontSize: 16, fontWeight: 600, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        }}>
          <ArrowLeft size={16} /> Voltar
        </button>
      </div>

      <div style={{ textAlign: 'center', padding: '16px 0 24px', fontSize: 12, color: T.label3 }}>Feito por Barbx</div>
    </div>
  );
};

export default BarberStep;
